import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import JourneyScene, { avatarFor } from "../../../components/crossroads/JourneyScene";
import useLiveScene from "../../../components/crossroads/useLiveScene";
import { LearningContent } from "../../../components/crossroads/CrossroadsUi";
import { pathColorsFor, readableTextOn, themeCssVars } from "../../../utils/crossroadsTheme";
import { countVotes, isOnline, voteKey } from "../../../utils/liveFirebase";
import useLiveHost, { PHASE_LABEL } from "./useLiveHost";
import "../../../components/crossroads/crossroads.css";
import "./Present.css";

const CONTROL_BAR = 84; // px the host's control bar covers at the bottom of the board
const TOP_BAR = 72; // px the title / timer strip covers at the top

function useFullscreen() {
  const [on, setOn] = useState(Boolean(document.fullscreenElement));
  useEffect(() => {
    const sync = () => setOn(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);
  const toggle = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    else document.documentElement.requestFullscreen?.().catch(() => {});
  }, []);
  return [on, toggle];
}

/**
 * Host's presenter view: the players' board, full screen, for a projector or a shared screen —
 * every connected player walks the roads, and the host steps the game on with the control bar
 * (or the keyboard / a presentation clicker: → / Space / PageDown = next, ← / PageUp = back).
 */
export default function Present() {
  const navigate = useNavigate();
  const host = useLiveHost();
  const { content, loadError, live, participants, runVotes, theme, questions, total, phase, index, question, counts, online, remainingMs, votingStarted, offset, correct, busy, summary, nextStep, prevStep, finishGame } = host;
  const { scenePhase, checkpointKey, sceneIndex, onPhaseDone } = useLiveScene(live, phase, index);
  const [panelHidden, setPanelHidden] = useState(false);
  const [playersOpen, setPlayersOpen] = useState(false);
  const [fullscreen, toggleFullscreen] = useFullscreen();

  // A new step always brings its panel back.
  useEffect(() => setPanelHidden(false), [phase, index]);

  const exitPresentation = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    navigate("/admin/live");
  }, [navigate]);

  const endPresentation = async () => {
    if (phase === "lobby" || phase === "complete") {
      exitPresentation();
      return;
    }
    const r = await Swal.fire({
      title: "End the presentation?",
      text: "Finish the game for every player, or just leave this screen and keep running it from Live Control.",
      icon: "question",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Finish game & exit",
      denyButtonText: "Just exit",
      cancelButtonText: "Keep presenting",
    });
    if (r.isConfirmed) {
      await finishGame();
      exitPresentation();
    } else if (r.isDenied) exitPresentation();
  };

  // Keyboard / clicker control.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest?.("input, textarea, select, .swal2-container")) return;
      if (busy) return;
      if (["ArrowRight", "PageDown", " "].includes(e.key) && nextStep) {
        e.preventDefault();
        nextStep.go();
      } else if (["ArrowLeft", "PageUp"].includes(e.key) && prevStep) {
        e.preventDefault();
        prevStep.go();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, nextStep, prevStep]);

  const players = useMemo(
    () => Object.entries(participants).filter(([, p]) => isOnline(p)).map(([id, p]) => ({ id, name: p.name || "Player" })),
    [participants]
  );
  const avatars = useMemo(() => players.map((p) => avatarFor(p.id, p.name)), [players]);
  const votesNow = runVotes?.[voteKey(index)] || {};

  if (!host.token) return <div className="pr-root pr-message">Open the admin panel from your game link to present a live game.</div>;
  if (loadError) return <div className="pr-root pr-message">{loadError}</div>;
  if (!content || live === undefined) return <div className="pr-root pr-message">Loading the presentation…</div>;

  const sceneQuestion = questions[sceneIndex] || null;
  const onBoard = phase !== "lobby" && phase !== "intro";
  const upcoming = questions[sceneIndex + 1];
  const colors = sceneQuestion ? pathColorsFor(sceneQuestion, theme) : { a: theme.colors.pathA, b: theme.colors.pathB };
  const showSplit = ["closed", "reveal", "learning"].includes(phase);
  const split = showSplit ? { a: counts.a, b: counts.b } : null;
  const pct = (n) => (counts.total ? Math.round((n / counts.total) * 100) : 0);
  const qColors = question ? pathColorsFor(question, theme) : colors;
  const secs = Math.ceil(remainingMs / 1000);

  let panel = null;
  if (phase === "lobby") {
    panel = (
      <div className="pr-card">
        <div className="pr-eyebrow">Live game</div>
        <h1 className="pr-title">{theme.title}</h1>
        <p className="pr-text">Open the game from your link and keep the screen on — we'll start in a moment.</p>
        <div className="pr-big-count">
          <strong>{online}</strong> player{online === 1 ? "" : "s"} connected
        </div>
        {players.length ? (
          <div className="pr-chips">
            {players.slice(0, 24).map((p) => {
              const a = avatarFor(p.id, p.name);
              return (
                <span key={p.id} className="pr-chip">
                  <span aria-hidden>{a.emoji}</span> {p.name}
                </span>
              );
            })}
            {players.length > 24 ? <span className="pr-chip pr-chip--more">+{players.length - 24} more</span> : null}
          </div>
        ) : null}
      </div>
    );
  } else if (phase === "intro") {
    panel = (
      <div className="pr-card">
        <div className="pr-eyebrow">Starting now</div>
        <h1 className="pr-title">{theme.introTitle}</h1>
        {theme.introText ? <p className="pr-text pr-pre">{theme.introText}</p> : null}
      </div>
    );
  } else if ((phase === "reveal" || phase === "learning") && question) {
    panel =
      phase === "reveal" ? (
        <div className="pr-card">
          <div className="pr-eyebrow">
            Crossroads {index + 1} of {total} · Results
          </div>
          <div className="pr-results">
            {[
              ["A", question.option_one, qColors.a, counts.a],
              ["B", question.option_two, qColors.b, counts.b],
            ].map(([letter, text, bg, n]) => (
              <div key={letter} className={`pr-result${correct === letter ? " is-correct" : ""}${correct && correct !== letter ? " is-other" : ""}`}>
                <div className="pr-result-row">
                  <span className="pr-letter" style={{ background: bg, color: readableTextOn(bg) }}>
                    {letter}
                  </span>
                  <span className="pr-result-text">{text}</span>
                  <strong className="pr-result-pct">{pct(n)}%</strong>
                </div>
                <div className="pr-bar">
                  <span style={{ width: `${pct(n)}%`, background: bg }} />
                </div>
                <div className="pr-result-votes">
                  {n} vote{n === 1 ? "" : "s"}
                  {correct === letter ? <span className="pr-correct-tag">✓ Correct path</span> : null}
                </div>
              </div>
            ))}
          </div>
          {!correct ? <p className="pr-text pr-center">There's no single right road here — this is how everyone chose.</p> : null}
        </div>
      ) : (
        <div className="pr-card pr-card--learning">
          <div className="pr-eyebrow">💡 Learning moment</div>
          <h1 className="pr-title">{question.dyk_heading || theme.dykHeading}</h1>
          <div className="pr-text pr-pre">
            <LearningContent question={question} />
          </div>
        </div>
      );
  } else if (phase === "complete") {
    panel = (
      <div className="pr-card">
        <div className="pr-eyebrow">🏁 Destination reached</div>
        <h1 className="pr-title">{theme.completionTitle}</h1>
        <div className="pr-kpis">
          <div>
            <strong>{summary.voters}</strong>
            <span>Players voted</span>
          </div>
          <div>
            <strong>{summary.votes}</strong>
            <span>Total votes</span>
          </div>
          {theme.hasCorrectAnswers ? (
            <div>
              <strong>{summary.accuracy}%</strong>
              <span>Audience accuracy</span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="cr-root pr-root" style={themeCssVars(theme)}>
      {host.unsavedResults ? (
        <div className="pr-save-warn" role="alert">
          {host.unsavedResults} result{host.unsavedResults === 1 ? "" : "s"} not saved to Reports yet — retrying…
        </div>
      ) : null}
      <div className="pr-stage">
        <JourneyScene
          phase={scenePhase}
          checkpointKey={checkpointKey}
          pathColors={colors}
          optionA={sceneQuestion?.option_one}
          optionB={sceneQuestion?.option_two}
          scenarioLabel={sceneQuestion && onBoard ? `Scenario ${sceneIndex + 1}` : ""}
          scenarioText={onBoard ? sceneQuestion?.question_name : ""}
          next={
            upcoming
              ? {
                  label: `Scenario ${sceneIndex + 2}`,
                  text: upcoming.question_name,
                  optionA: upcoming.option_one,
                  optionB: upcoming.option_two,
                  pathColors: pathColorsFor(upcoming, theme),
                }
              : null
          }
          destinationNext={phase === "complete" || sceneIndex + 1 >= total}
          idleTitle={phase === "intro" ? theme.introTitle : theme.title}
          idleText={phase === "intro" ? "" : "Waiting for players"}
          backgroundUrl={theme.images.journeyDesktop}
          backgroundUrlPortrait={theme.images.journeyPortrait}
          avatars={avatars}
          split={split}
          votes={runVotes?.[voteKey(sceneIndex)] || {}}
          reveal={phase === "reveal" || phase === "learning" ? correct : null}
          footerLabel={phase === "voting" ? "Vote from your device" : "Choose your path"}
          onPhaseDone={onPhaseDone}
          insetTop={TOP_BAR}
          insetBottom={CONTROL_BAR}
          timerEndsAt={votingStarted && remainingMs > 0 ? live.votingEndsAt - offset : 0}
          timerTotal={theme.votingSeconds * 1000}
          timerSeconds={secs}
        />
      </div>

      {/* Top strip: where we are + the voting timer */}
      <div className="pr-top">
        <div className="pr-top-left">
          {question ? (
            <span className="pr-pill">
              Crossroads {index + 1} / {total}
            </span>
          ) : (
            <span className="pr-pill">{theme.title}</span>
          )}
        </div>
        <div className="pr-top-right">
          {phase === "voting" ? (
            <>
              <span className="pr-pill">
                {countVotes(votesNow).total} / {online} voted
              </span>
            </>
          ) : null}
          {phase === "closed" ? <span className="pr-pill">Voting closed · {counts.total} votes</span> : null}
        </div>
      </div>

      {panel && !panelHidden ? <div className="pr-panel">{panel}</div> : null}

      {playersOpen ? (
        <aside className="pr-drawer" aria-label="Connected players">
          <div className="pr-drawer-head">
            <strong>
              {online} player{online === 1 ? "" : "s"} connected
            </strong>
            <button type="button" className="pr-icon-btn" onClick={() => setPlayersOpen(false)} aria-label="Close player list">
              <i className="fa-solid fa-xmark" aria-hidden />
            </button>
          </div>
          <ul className="pr-drawer-list">
            {players.map((p) => {
              const a = avatarFor(p.id, p.name);
              const voted = question ? votesNow[p.id] : null;
              return (
                <li key={p.id}>
                  <span className="pr-drawer-avatar" style={{ borderColor: a.ring }} aria-hidden>
                    {a.emoji}
                  </span>
                  <span className="pr-drawer-name">{p.name}</span>
                  {question ? <span className={`pr-drawer-vote${voted ? " is-voted" : ""}`}>{voted ? "✓ Voted" : "—"}</span> : null}
                </li>
              );
            })}
            {!players.length ? <li className="pr-muted">No one has joined yet.</li> : null}
          </ul>
        </aside>
      ) : null}

      {/* Host control bar */}
      <div className="pr-controls" role="toolbar" aria-label="Presentation controls">
        <button type="button" className="pr-btn" onClick={() => prevStep?.go()} disabled={!prevStep || busy} title={prevStep?.label || ""}>
          <i className="fa-solid fa-chevron-left" aria-hidden /> Previous
        </button>

        <div className="pr-status">
          <span className={`pr-dot pr-dot--${phase}`} aria-hidden />
          <span className="pr-status-text">
            {PHASE_LABEL[phase] || phase}
            {question ? ` · Crossroads ${index + 1}/${total}` : ""}
          </span>
        </div>

        <button type="button" className="pr-btn pr-btn--next" onClick={() => nextStep?.go()} disabled={!nextStep || busy}>
          {nextStep ? (
            <>
              <i className={`fa-solid ${nextStep.icon}`} aria-hidden /> {nextStep.label}
            </>
          ) : (
            "End of game"
          )}
          <i className="fa-solid fa-chevron-right" aria-hidden />
        </button>

        <div className="pr-controls-right">
          <button type="button" className={`pr-btn pr-btn--ghost${playersOpen ? " is-active" : ""}`} onClick={() => setPlayersOpen((o) => !o)} aria-pressed={playersOpen}>
            <i className="fa-solid fa-users" aria-hidden /> {online}
            <span className="pr-hide-sm"> connected</span>
          </button>
          {panel ? (
            <button type="button" className="pr-btn pr-btn--ghost" onClick={() => setPanelHidden((h) => !h)} aria-pressed={!panelHidden} title={panelHidden ? "Show the panel" : "Hide the panel to see the board"}>
              <i className={`fa-solid ${panelHidden ? "fa-eye" : "fa-eye-slash"}`} aria-hidden />
              <span className="pr-hide-sm"> {panelHidden ? "Show panel" : "Hide panel"}</span>
            </button>
          ) : null}
          <button type="button" className="pr-btn pr-btn--ghost" onClick={toggleFullscreen} title={fullscreen ? "Exit full screen" : "Full screen"}>
            <i className={`fa-solid ${fullscreen ? "fa-compress" : "fa-expand"}`} aria-hidden />
          </button>
          <button type="button" className="pr-btn pr-btn--end" onClick={endPresentation}>
            <i className="fa-solid fa-stop" aria-hidden />
            <span className="pr-hide-sm"> End presentation</span>
          </button>
        </div>
      </div>
    </div>
  );
}
